<template>
  <div class="analytics-panel">
    <div class="analytics-toolbar">
      <div class="filter-field">
        <label>統計版本</label>
        <select v-model="selectedVersionId" class="form-control" @change="handleVersionChange">
          <option v-if="versions.length === 0" value="">尚無統計版本</option>
          <option v-for="version in versions" :key="version.id" :value="version.id">
            第 {{ version.versionNo }} 版{{ version.status === 'current' ? '（目前版本）' : '' }}
          </option>
        </select>
      </div>
      <div class="filter-field">
        <label>開始日期</label>
        <input v-model="filters.from" type="date" class="form-control" @change="applyFilters" />
      </div>
      <div class="filter-field">
        <label>結束日期</label>
        <input v-model="filters.to" type="date" class="form-control" @change="applyFilters" />
      </div>
      <button class="btn btn-secondary" :disabled="loading" @click="refresh">
        <i :class="loading ? 'ri-loader-4-line spin' : 'ri-refresh-line'"></i>
        重新整理
      </button>
      <button
        class="btn btn-export"
        :disabled="exporting || !selectedVersionId"
        @click="exportClicks"
      >
        <i :class="exporting ? 'ri-loader-4-line spin' : 'ri-download-2-line'"></i>
        {{ exporting ? '匯出中' : '匯出 CSV' }}
      </button>
    </div>

    <div v-if="summary.trackingEnabled" class="summary-cards">
      <div class="summary-card">
        <span class="summary-label">總點擊數</span>
        <strong>{{ formatNumber(summary.totalClicks) }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">不重複點擊人數</span>
        <strong>{{ formatNumber(summary.totalUniqueClicks) }}</strong>
      </div>
      <div class="summary-card">
        <span class="summary-label">按鈕數</span>
        <strong>{{ formatNumber(summary.buttons.length) }}</strong>
      </div>
    </div>

    <div v-if="selectedVersion && selectedVersion.detailsRetainUntil" class="retention-notice">
      <i class="ri-information-line"></i>
      <span>
        點擊明細保留 360 天，此版本明細預計保留至
        <strong>{{ formatDateTime(selectedVersion.detailsRetainUntil) }}</strong>；彙總數字不受影響。
      </span>
    </div>

    <div v-if="!loading && !summary.trackingEnabled" class="empty-state">
      <i class="ri-bar-chart-box-line"></i>
      <p>尚未啟用點擊追蹤</p>
      <span>選單下次部署後會建立統計版本並開始累計。</span>
    </div>

    <template v-else>
      <div class="table-wrapper">
        <div v-if="loading" class="loading-state">
          <i class="ri-loader-4-line spin"></i> 載入點擊明細中
        </div>
        <table v-else-if="clicks.length > 0">
          <thead>
            <tr>
              <th>點擊時間</th>
              <th>Tab / 按鈕</th>
              <th>動作類型</th>
              <th>LINE User ID</th>
              <th>會員資料</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="click in clicks" :key="click.id">
              <td class="nowrap">{{ formatDateTime(click.clickedAt) }}</td>
              <td>
                <div class="button-name">{{ click.label || `按鈕 ${click.areaIndex + 1}` }}</div>
                <span class="cell-note">Tab {{ click.tabIndex + 1 }}・區塊 {{ click.areaIndex + 1 }}</span>
              </td>
              <td>{{ actionTypeLabel(click.actionType) }}</td>
              <td class="line-user-id">{{ click.lineUserId || '—' }}</td>
              <td>
                <template v-if="click.isMember">
                  <div>{{ click.memberName || '未命名會員' }}</div>
                  <span class="cell-note">{{ click.memberCode || '無會員編號' }}</span>
                </template>
                <span v-else class="non-member-badge">非會員</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="summary.trackingEnabled" class="empty-state compact">
          <i class="ri-cursor-line"></i>
          <p>此篩選期間尚無點擊明細</p>
        </div>
      </div>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.total"
        @change="goPage"
      />
    </template>
  </div>
</template>

<script>
import { lineRichMenuService } from '@/api/modules/lineRichMenu.js';
import { showError } from '@/utils/swal.js';

function toDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default {
  name: 'RichMenuAnalyticsPanel',
  props: {
    menuId: { type: String, required: true },
    initialAnalytics: { type: Object, default: () => ({}) }
  },
  emits: ['summary-loaded'],
  data() {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    return {
      loading: false,
      exporting: false,
      versions: [],
      selectedVersionId: '',
      summary: {
        trackingEnabled: false,
        totalClicks: 0,
        totalUniqueClicks: 0,
        buttons: []
      },
      clicks: [],
      filters: {
        from: toDateInput(from),
        to: toDateInput(today)
      },
      pagination: {
        page: 1,
        pageSize: 50,
        total: 0,
        totalPages: 0
      }
    };
  },
  computed: {
    selectedVersion() {
      return this.versions.find(version => version.id === this.selectedVersionId)
        || this.summary.version
        || null;
    }
  },
  async created() {
    if (this.initialAnalytics?.trackingEnabled) {
      this.summary = this.normalizeSummary(this.initialAnalytics);
      this.$emit('summary-loaded', this.summary);
    }
    await this.loadVersions();
    await this.refresh();
  },
  methods: {
    unwrap(response) {
      return response?.data ?? response ?? {};
    },
    normalizeSummary(summary) {
      return {
        trackingEnabled: summary?.trackingEnabled === true,
        version: summary?.version || null,
        totalClicks: Number(summary?.totalClicks || 0),
        totalUniqueClicks: Number(summary?.totalUniqueClicks || 0),
        buttons: Array.isArray(summary?.buttons) ? summary.buttons : []
      };
    },
    async loadVersions() {
      try {
        const response = await lineRichMenuService.getAnalyticsVersions(this.menuId);
        const data = this.unwrap(response);
        this.versions = Array.isArray(data) ? data : [];
        const current = this.versions.find(version => version.status === 'current');
        this.selectedVersionId = current?.id || this.versions[0]?.id || '';
      } catch (error) {
        showError('載入統計版本失敗', error.response?.data?.message || error.message || '');
      }
    },
    async refresh() {
      this.loading = true;
      try {
        const versionId = this.selectedVersionId || undefined;
        const [summaryResponse, clicksResponse] = await Promise.all([
          lineRichMenuService.getAnalyticsSummary(this.menuId, versionId),
          lineRichMenuService.getAnalyticsClicks(this.menuId, this.buildParams())
        ]);
        this.summary = this.normalizeSummary(this.unwrap(summaryResponse));
        this.$emit('summary-loaded', this.summary);

        const clickData = this.unwrap(clicksResponse);
        this.clicks = clickData?.items || [];
        const pageData = clickData?.pagination || {};
        this.pagination = {
          page: Number(pageData.page || this.pagination.page),
          pageSize: Number(pageData.pageSize || 50),
          total: Number(pageData.total ?? pageData.totalItems ?? 0),
          totalPages: Number(pageData.totalPages || 0)
        };
      } catch (error) {
        showError('載入點擊分析失敗', error.response?.data?.message || error.message || '');
      } finally {
        this.loading = false;
      }
    },
    handleVersionChange() {
      this.pagination.page = 1;
      this.refresh();
    },
    applyFilters() {
      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        showError('日期範圍不正確', '開始日期不可晚於結束日期');
        return;
      }
      this.pagination.page = 1;
      this.refresh();
    },
    goPage(page) {
      this.pagination.page = page;
      this.refresh();
    },
    buildParams(includePagination = true) {
      const params = {
        versionId: this.selectedVersionId || undefined,
        from: this.filters.from ? this.dateBoundary(this.filters.from, false) : undefined,
        to: this.filters.to ? this.dateBoundary(this.filters.to, true) : undefined
      };
      if (includePagination) {
        params.page = this.pagination.page;
        params.pageSize = this.pagination.pageSize;
      }
      return params;
    },
    async exportClicks() {
      this.exporting = true;
      try {
        const blob = await lineRichMenuService.exportAnalyticsClicks(
          this.menuId,
          this.buildParams(false)
        );
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const versionNo = this.selectedVersion?.versionNo || 'current';
        link.download = `圖文選單點擊明細_第${versionNo}版_${this.filters.from}_${this.filters.to}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        showError('匯出失敗', error.response?.data?.message || error.message || '');
      } finally {
        this.exporting = false;
      }
    },
    dateBoundary(value, endOfDay) {
      const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
      return date.toISOString();
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('zh-TW');
    },
    formatDateTime(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    actionTypeLabel(type) {
      return {
        uri: '開啟網址',
        message: '發送文字訊息',
        postback: 'Postback'
      }[type] || type || '—';
    }
  }
};
</script>

<style scoped>
.analytics-panel { display: flex; flex-direction: column; gap: 16px; }
.analytics-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}
.filter-field { display: flex; flex-direction: column; gap: 5px; }
.filter-field label { color: #6b7280; font-size: 12px; font-weight: 600; }
.filter-field .form-control { min-width: 150px; }
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 38px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.btn:disabled { cursor: not-allowed; opacity: .55; }
.btn-secondary { border: 1px solid #d1d5db; background: #fff; color: #374151; }
.btn-export { border: 1px solid #06c755; background: #06c755; color: #fff; }
.summary-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.summary-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border: 1px solid #d1fae5;
  border-radius: 10px;
  background: #f0fdf4;
}
.summary-label { color: #6b7280; font-size: 12px; }
.summary-card strong { color: #15803d; font-size: 24px; }
.retention-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 11px 14px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1e40af;
  font-size: 13px;
  line-height: 1.5;
}
.retention-notice i { margin-top: 2px; flex-shrink: 0; }
.table-wrapper { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 10px; }
table { width: 100%; min-width: 850px; border-collapse: collapse; font-size: 13px; }
th {
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
}
td { padding: 11px 12px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
tbody tr:last-child td { border-bottom: none; }
tbody tr:hover td { background: #f9fafb; }
.nowrap { white-space: nowrap; }
.button-name { color: #111827; font-weight: 500; }
.cell-note { color: #9ca3af; font-size: 11px; }
.line-user-id {
  max-width: 190px;
  overflow: hidden;
  color: #6b7280;
  font-family: monospace;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.non-member-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
}
.empty-state, .loading-state {
  padding: 50px 20px;
  color: #9ca3af;
  text-align: center;
}
.empty-state i { display: block; margin-bottom: 8px; font-size: 36px; }
.empty-state p { margin: 0 0 4px; color: #6b7280; font-weight: 600; }
.empty-state span { font-size: 12px; }
.empty-state.compact { padding: 36px 20px; }
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6b7280;
  font-size: 12px;
}
.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}
.page-btn:disabled { cursor: not-allowed; opacity: .45; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .analytics-toolbar { align-items: stretch; }
  .filter-field, .filter-field .form-control { width: 100%; }
  .summary-cards { grid-template-columns: 1fr; }
  .btn { flex: 1; }
}
</style>
